import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { faceCounts, hasStraight, rollDice } from '@campfire/dice';

export interface FarkleOptions {
  /** Points to reach (the others then get one last turn). */
  readonly target: 2500 | 5000 | 10000;
  /** Turn points needed to get on the board; 0 = none. */
  readonly entry: 0 | 500;
  /** doubling: 4/5/6 of a kind double the three-of-a-kind value each time. fixed: 1000 / 2000 / 3000. */
  readonly kinds: 'doubling' | 'fixed';
}

/** After this many rounds the game ends with the high score (a safety stop; rarely reached). */
export const ROUND_LIMIT = 60;

export type FarklePhase = 'roll' | 'keep' | 'decide';

export type FarkleEvent =
  | { readonly kind: 'roll'; readonly seat: Seat; readonly dice: readonly number[] }
  | { readonly kind: 'farkle'; readonly seat: Seat; readonly dice: readonly number[]; readonly lost: number }
  | { readonly kind: 'keep'; readonly seat: Seat; readonly dice: readonly number[]; readonly points: number; readonly hot: boolean }
  | { readonly kind: 'bank'; readonly seat: Seat; readonly points: number; readonly total: number };

export interface FarkleState {
  readonly options: FarkleOptions;
  readonly scores: readonly number[];
  readonly turn: Seat;
  readonly round: number;
  readonly phase: FarklePhase;
  /** Dice just rolled and not yet set aside. */
  readonly dice: readonly number[];
  /** Dice set aside so far this turn. */
  readonly kept: readonly number[];
  /** Indexes into `dice` the player has tapped to set aside; everyone sees them (FR-FARKLE-13). */
  readonly picked?: readonly number[];
  readonly turnPoints: number;
  /** Seat that reached the target; everyone else gets one more turn. */
  readonly finalSeat: Seat | null;
  readonly last: FarkleEvent | null;
  readonly result: Outcome | null;
}

export type FarkleAction =
  | { readonly type: 'roll' }
  /** Set aside these dice; without `dice`, the ones picked on the table. */
  | { readonly type: 'keep'; readonly dice?: readonly number[] }
  /** Tap a die to mark it for setting aside, or tap again to unmark it. */
  | { readonly type: 'pick'; readonly index: number }
  | { readonly type: 'bank' };

export interface FarkleView {
  readonly you: Seat | null;
  readonly scores: readonly number[];
  readonly turn: Seat;
  readonly round: number;
  readonly phase: FarklePhase;
  readonly picked: readonly number[];
  readonly dice: readonly number[];
  readonly kept: readonly number[];
  readonly turnPoints: number;
  /** Dice you would roll next (6 after hot dice). */
  readonly toRoll: number;
  readonly onBoard: readonly boolean[];
  readonly canBank: boolean;
  readonly entry: number;
  readonly target: number;
  readonly finalSeat: Seat | null;
  readonly last: FarkleEvent | null;
  readonly result: Outcome | null;
}

/**
 * Points when EVERY die in `dice` is part of a scoring combination, else null.
 * Straight and three pairs (six dice) score 1500; triples and better by face; stray 1s and 5s score singly.
 */
export function scoreSet(dice: readonly number[], kinds: 'doubling' | 'fixed' = 'doubling'): number | null {
  if (dice.length === 0) return null;
  const c = faceCounts(dice);
  if (dice.length === 6) {
    if (hasStraight(dice, 6)) return 1500;
    if (c.filter((n) => n === 2).length === 3) return 1500;
  }
  let total = 0;
  for (let f = 1; f <= 6; f++) {
    const n = c[f] as number;
    if (n === 0) continue;
    if (n >= 3) {
      const triple = f === 1 ? 1000 : f * 100;
      total += kinds === 'fixed' ? (n === 3 ? triple : (n - 3) * 1000) : triple * 2 ** (n - 3);
    } else if (f === 1) total += n * 100;
    else if (f === 5) total += n * 50;
    else return null;
  }
  return total;
}

/** Does this roll contain at least one scoring die? */
export function hasScoring(dice: readonly number[]): boolean {
  const c = faceCounts(dice);
  return (c[1] as number) > 0 || (c[5] as number) > 0 || c.some((n) => n >= 3) || scoreSet(dice) === 1500;
}

/** Every valid set-aside choice (as sorted index lists) for a roll. */
export function keepChoices(dice: readonly number[], kinds: 'doubling' | 'fixed'): number[][] {
  const out: number[][] = [];
  for (let mask = 1; mask < 1 << dice.length; mask++) {
    const idx = dice.flatMap((_, i) => (mask & (1 << i) ? [i] : []));
    if (scoreSet(idx.map((i) => dice[i] as number), kinds) !== null) out.push(idx);
  }
  return out;
}

const free = (s: FarkleState): number => {
  const left = 6 - s.kept.length;
  return left === 0 ? 6 : left;
};

const canBank = (s: FarkleState): boolean =>
  s.phase === 'decide' && s.turnPoints > 0 && ((s.scores[s.turn] as number) > 0 || s.turnPoints >= s.options.entry);

function finish(s: FarkleState): FarkleState {
  const best = Math.max(...s.scores);
  const winners = s.scores.flatMap((v, i) => (v === best ? [i] : []));
  return { ...s, result: { winners, draw: false, reason: s.finalSeat === null ? 'round limit' : 'target', scores: { ...s.scores } } };
}

/** Hand the dice to the next seat, ending the game if the final lap is complete. */
function advance(s: FarkleState): FarkleState {
  const turn = (s.turn + 1) % s.scores.length;
  const round = turn === 0 ? s.round + 1 : s.round;
  const next: FarkleState = { ...s, turn, round, phase: 'roll', dice: [], kept: [], picked: [], turnPoints: 0 };
  if (turn === s.finalSeat || round > ROUND_LIMIT) return finish(next);
  return next;
}

export const farkle: GameDefinition<FarkleState, FarkleAction, FarkleView, FarkleOptions> = {
  id: 'farkle',
  name: 'Farkle',
  rules: [
    'Push your luck with six dice and be first to reach the target score. Everyone else then gets one last turn.',
    'On your turn roll the dice. Set aside at least one scoring die, then either bank your turn points or roll the rest to try for more.',
    'Scoring: each 1 is 100 and each 5 is 50. Three of a kind is the face times 100 (three 1s is 1,000). A six-dice straight or three pairs scores 1,500 and has to be taken in one go.',
    'Every die you set aside must be part of a scoring combination. Combinations count only within a single roll, so a pair of 2s from one roll cannot join a 2 from the next.',
    'If you set aside all six dice you have hot dice and roll all six again.',
    'If a roll has no scoring dice you Farkle: you lose everything from this turn and the dice pass on.',
    'Setting: Game length (first to 2,500, 5,000 or 10,000). The player who gets there triggers one last turn for each other player; the highest score wins.',
    'Setting: Getting on the board. By default you need 500 or more banked in a single turn before your score counts; choose No minimum to skip that.',
    'Setting: Four, five, six of a kind. Each extra die doubles the three-of-a-kind score, or use a fixed 1,000 / 2,000 / 3,000.',
    'The app enforces all scoring. A safety stop ends the game after 60 rounds with the highest score winning.',
  ],
  minSeats: 2,
  maxSeats: 8,
  defaultOptions: { target: 10000, entry: 500, kinds: 'doubling' },
  optionSpecs: [
    {
      key: 'target',
      label: 'Game length',
      help: 'When someone reaches the target, everyone else gets one last turn.',
      choices: [
        { value: 2500, label: 'First to 2,500' },
        { value: 5000, label: 'First to 5,000' },
        { value: 10000, label: 'First to 10,000' },
      ],
    },
    {
      key: 'entry',
      label: 'Getting on the board',
      choices: [
        { value: 500, label: 'Bank 500 or more in one turn first' },
        { value: 0, label: 'No minimum' },
      ],
    },
    {
      key: 'kinds',
      label: 'Four, five, six of a kind',
      choices: [
        { value: 'doubling', label: 'Each extra die doubles the score' },
        { value: 'fixed', label: 'Fixed 1,000 / 2,000 / 3,000' },
      ],
    },
  ],

  setup: ({ seats, options }) => ({
    options,
    scores: Array.from({ length: seats }, () => 0),
    turn: 0,
    round: 1,
    phase: 'roll',
    dice: [],
    kept: [],
    turnPoints: 0,
    finalSeat: null,
    last: null,
    result: null,
  }),

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (s.result) return { ok: false, reason: 'game over' };
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    switch (a.type) {
      case 'roll':
        return s.phase === 'keep' ? { ok: false, reason: 'set aside scoring dice first' } : { ok: true };
      case 'bank':
        if (s.phase !== 'decide') return { ok: false, reason: 'set aside scoring dice first' };
        return canBank(s) ? { ok: true } : { ok: false, reason: `you need ${s.options.entry} in one turn to get on the board` };
      case 'pick':
        if (s.phase !== 'keep') return { ok: false, reason: 'roll first' };
        return Number.isInteger(a.index) && a.index >= 0 && a.index < s.dice.length ? { ok: true } : { ok: false, reason: 'no such die' };
      case 'keep': {
        if (s.phase !== 'keep') return { ok: false, reason: 'roll first' };
        const idx = a.dice ?? s.picked ?? [];
        if (!Array.isArray(idx) || idx.length === 0) return { ok: false, reason: 'choose at least one die' };
        if (new Set(idx).size !== idx.length || idx.some((i) => !Number.isInteger(i) || i < 0 || i >= s.dice.length)) return { ok: false, reason: 'bad dice' };
        return scoreSet(idx.map((i) => s.dice[i] as number), s.options.kinds) === null ? { ok: false, reason: 'every die you set aside must score' } : { ok: true };
      }
      default:
        return { ok: false, reason: 'unknown action' };
    }
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    if (a.type === 'roll') {
      const dice = rollDice(free(s), rng);
      if (!hasScoring(dice)) {
        return advance({ ...s, last: { kind: 'farkle', seat, dice, lost: s.turnPoints } });
      }
      return { ...s, phase: 'keep', dice, picked: [], kept: s.kept.length >= 6 ? [] : s.kept, last: { kind: 'roll', seat, dice } };
    }
    if (a.type === 'pick') {
      const picked = s.picked ?? [];
      return { ...s, picked: picked.includes(a.index) ? picked.filter((i) => i !== a.index) : [...picked, a.index].sort((x, y) => x - y) };
    }
    if (a.type === 'keep') {
      const idx = new Set(a.dice ?? s.picked ?? []);
      const set = s.dice.filter((_, i) => idx.has(i));
      const points = scoreSet(set, s.options.kinds) as number;
      const kept = [...s.kept, ...set];
      return { ...s, phase: 'decide', dice: [], picked: [], kept, turnPoints: s.turnPoints + points, last: { kind: 'keep', seat, dice: set, points, hot: kept.length === 6 } };
    }
    const total = (s.scores[seat] as number) + s.turnPoints;
    const scores = s.scores.map((v, i) => (i === seat ? total : v));
    const finalSeat = s.finalSeat === null && total >= s.options.target ? seat : s.finalSeat;
    return advance({ ...s, scores, finalSeat, last: { kind: 'bank', seat, points: s.turnPoints, total } });
  },

  view(s, viewer: Viewer): FarkleView {
    return {
      you: viewer.kind === 'seat' ? viewer.seat : null,
      scores: s.scores,
      turn: s.turn,
      round: s.round,
      phase: s.phase,
      dice: s.dice,
      picked: s.result ? [] : (s.picked ?? []),
      kept: s.kept,
      turnPoints: s.turnPoints,
      toRoll: free(s),
      onBoard: s.scores.map((v) => v > 0),
      canBank: !s.result && canBank(s),
      entry: s.options.entry,
      target: s.options.target,
      finalSeat: s.finalSeat,
      last: s.last,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== s.turn) return [];
    if (s.phase === 'keep') return keepChoices(s.dice, s.options.kinds).map((dice) => ({ type: 'keep', dice }));
    const out: FarkleAction[] = [{ type: 'roll' }];
    if (canBank(s)) out.push({ type: 'bank' });
    return out;
  },
};
